import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Edit, Trash2, Download, TrendingDown, Search } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import * as XLSX from 'xlsx';
import RecordExpenseModal from './RecordExpenseModal';
import EditExpenseModal from './EditExpenseModal';
import { DateChip, MultiChip, FilterButton, FiltersSheet, dateChipLabel } from './_filterChips';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExpensesList = () => {
  const { token } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  // Filters — honor ?range= URL hint from Home KPI clicks (today/month/yesterday).
  const [searchParams] = useSearchParams();
  const initialDateRange = (() => {
    const r = searchParams.get('range');
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (r === 'today') return { preset: 'today', start: fmt(today), end: fmt(today) };
    if (r === 'month') {
      const ms = new Date(today.getFullYear(), today.getMonth(), 1);
      const me = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { preset: 'this_month', start: fmt(ms), end: fmt(me) };
    }
    return { preset: 'all', start: '', end: '' };
  })();
  const [categoryFilter, setCategoryFilter] = useState([]); // multi-select
  const [propertyFilter, setPropertyFilter] = useState([]);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [expensesRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/money/expenses`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setExpenses(expensesRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useDataSync(EVT.EXPENSE, fetchData);

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setShowEditModal(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await axios.delete(`${API}/money/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      emit(EVT.EXPENSE, { action: 'deleted', id: expenseId });

      toast({
        title: "Expense Deleted",
        description: "Expense has been deleted successfully"
      });
      
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete expense",
        variant: "destructive"
      });
    }
  };

  const filteredExpenses = expenses.filter(e => {
    if (categoryFilter.length && !categoryFilter.includes(e.category)) return false;
    if (propertyFilter.length && !propertyFilter.includes(e.property_id)) return false;
    if (dateRange.start && (e.expense_date || '') < dateRange.start) return false;
    if (dateRange.end && (e.expense_date || '') > dateRange.end) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = [e.title, e.notes, e.vendor, e.category].filter(Boolean).join(' ').toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredExpenses);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');
    XLSX.writeFile(workbook, `expenses_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  const categories = ['maintenance', 'utilities', 'repairs', 'supplies', 'salaries', 'taxes', 'other'];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading expenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-6 space-y-6">
      {/* Header with iOS-style blur */}
      <div className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl border border-white/50 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Expenses</h1>
            <p className="text-gray-600 mt-2">Track and manage all business expenses</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={exportToExcel} variant="outline" className="rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-105">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-105">
              <Plus size={20} className="mr-2" />
              Add Expense
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Card with glassmorphism */}
      <div className="backdrop-blur-xl bg-gradient-to-r from-red-500/90 to-orange-500/90 rounded-3xl shadow-2xl border border-white/50 p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-red-100 text-sm font-medium">Total Expenses</p>
            <p className="text-5xl font-bold mt-2">₹{totalExpenses.toLocaleString()}</p>
            <p className="text-red-100 text-sm mt-2">{filteredExpenses.length} transactions</p>
          </div>
          <TrendingDown className="h-20 w-20 text-white/60" />
        </div>
      </div>

      {/* Category Filter with iOS pills */}
      <Card className="p-4 border border-gray-200">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="expenses-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, vendor, notes…"
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
          <DateChip
            testId="chip-expense-date"
            value={dateRange}
            onChange={setDateRange}
            label={dateChipLabel(dateRange, 'Date Range')}
            highlight={dateRange.preset !== 'all' && dateRange.preset !== ''}
          />
          <MultiChip
            testId="chip-expense-category"
            label={categoryFilter.length === 0 ? 'All Categories' :
                   categoryFilter.length === 1 ? categoryFilter[0] : `${categoryFilter.length} categories`}
            options={categories.map(c => ({ key: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))}
            selected={categoryFilter}
            onChange={setCategoryFilter}
            highlight={categoryFilter.length > 0}
          />
          <MultiChip
            testId="chip-expense-property"
            label={propertyFilter.length === 0 ? 'All Properties' :
                   propertyFilter.length === 1 ? (properties.find(p => p.id === propertyFilter[0])?.name || 'Property') : `${propertyFilter.length} properties`}
            options={properties.map(p => ({ key: p.id, label: p.name }))}
            selected={propertyFilter}
            onChange={setPropertyFilter}
            highlight={propertyFilter.length > 0}
          />
          <FilterButton
            testId="open-expense-filters"
            onClick={() => setSheetOpen(true)}
            count={
              (dateRange.preset !== 'all' && dateRange.preset ? 1 : 0) +
              (categoryFilter.length ? 1 : 0) +
              (propertyFilter.length ? 1 : 0)
            }
          />
        </div>
      </Card>

      {/* Expenses Grid */}
      <div className="grid grid-cols-1 gap-4">
        {filteredExpenses.length === 0 ? (
          <div className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl border border-white/50 p-12 text-center">
            <TrendingDown className="w-20 h-20 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Expenses Found</h3>
            <p className="text-gray-600">Add your first expense to get started</p>
          </div>
        ) : (
          filteredExpenses.map((expense) => (
            <div key={expense.id} className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl border border-white/50 p-6 hover:shadow-2xl transition-all hover:scale-[1.01]">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white shadow-lg">
                      <TrendingDown size={28} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{expense.description || expense.category}</h3>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs px-3 py-1 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 font-semibold">
                          {expense.category}
                        </span>
                        <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-800 font-semibold">
                          {expense.payment_method}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 text-xs">Amount</span>
                      <p className="font-bold text-red-600 text-lg">₹{expense.amount.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">Date</span>
                      <p className="font-medium text-gray-900">{expense.date}</p>
                    </div>
                    {expense.vendor_name && (
                      <div>
                        <span className="text-gray-500 text-xs">Vendor</span>
                        <p className="font-medium text-gray-900">{expense.vendor_name}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(expense)}
                    className="rounded-2xl border-blue-300 text-blue-600 hover:bg-blue-50 shadow-md hover:shadow-lg transition-all"
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(expense.id)}
                    className="rounded-2xl border-red-300 text-red-600 hover:bg-red-50 shadow-md hover:shadow-lg transition-all"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Expense Modal */}
      <RecordExpenseModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={fetchData}
        token={token}
      />

      {/* Edit Expense Modal */}
      <EditExpenseModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={fetchData}
        expense={selectedExpense}
        token={token}
      />

      <FiltersSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Expense Filters"
        sections={[
          { key: 'dateRange',  label: 'Date Range',  kind: 'date' },
          { key: 'categories', label: 'Categories',  kind: 'multi', options: categories.map(c => ({ key: c, label: c.charAt(0).toUpperCase() + c.slice(1) })) },
          { key: 'properties', label: 'Properties',  kind: 'multi', options: properties.map(p => ({ key: p.id, label: p.name })) },
        ]}
        values={{ dateRange, categories: categoryFilter, properties: propertyFilter }}
        onApply={(d) => { setDateRange(d.dateRange); setCategoryFilter(d.categories); setPropertyFilter(d.properties); }}
        onClear={() => { setDateRange({preset:'all',start:'',end:''}); setCategoryFilter([]); setPropertyFilter([]); setSearch(''); }}
      />
    </div>
  );
};

export default ExpensesList;